/**
 * Sentry beforeSend filter helpers.
 * Drops non-actionable errors (chunk loading, third-party-only) and adds
 * HTTP context (status + URL pattern) so errors group properly instead of appearing as <unknown>.
 */
import type { ErrorEvent, EventHint } from '@sentry/angular';

const CHUNK_LOAD_ERROR_PATTERNS = [
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'error loading dynamically imported module',
	'Loading chunk'
];

const THIRD_PARTY_PATH_PATTERNS = [/\/ckeditor\//, /node_modules\//];

export function isChunkLoadError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : String(error ?? '');
	return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

export function isHttpError(error: unknown): boolean {
	if (error && typeof error === 'object') {
		const rec = error as Record<string, unknown>;
		if ('status' in error && typeof rec.status === 'number' && 'statusText' in error) {
			return true;
		}
	}
	const message = error instanceof Error ? error.message : String(error ?? '');
	return message.includes('Http failure response');
}

export function extractHttpStatusCode(error: unknown): string {
	if (error && typeof error === 'object' && 'status' in error) {
		return String((error as Record<string, unknown>).status);
	}
	const message = error instanceof Error ? error.message : String(error ?? '');
	const match = message.match(/Http failure response for .+: (\d+)/);
	return match ? match[1] : 'unknown';
}

export function extractHttpUrlPattern(error: unknown): string {
	if (error && typeof error === 'object' && 'url' in error) {
		const url = (error as Record<string, unknown>).url;
		if (typeof url === 'string') {
			return sanitizeUrlPath(url);
		}
	}
	const message = error instanceof Error ? error.message : String(error ?? '');
	const match = message.match(/Http failure response for (.+): \d+/);
	return match ? sanitizeUrlPath(match[1]) : 'unknown';
}

export function sanitizeUrlPath(url: string): string {
	try {
		const parsed = new URL(url, 'http://placeholder');
		// Replace UUIDs before numeric IDs — otherwise /\d+/ mangles digits inside UUIDs.
		return parsed.pathname
			.replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '/:uuid')
			.replace(/\/\d+/g, '/:id');
	} catch {
		return url
			.replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '/:uuid')
			.replace(/\/\d+/g, '/:id');
	}
}

export function sentryBeforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
	const error = hint?.originalException;

	if (isChunkLoadError(error)) {
		return null;
	}

	if (isHttpError(error)) {
		const statusCode = extractHttpStatusCode(error);
		const urlPattern = extractHttpUrlPattern(error);
		event.tags = { ...event.tags, error_type: 'http', http_status: statusCode, http_url_pattern: urlPattern };
	}

	if (isThirdPartyOnlyError(event)) {
		return null;
	}

	return event;
}

export function isThirdPartyOnlyError(event: ErrorEvent): boolean {
	const frames = event.exception?.values?.[0]?.stacktrace?.frames;
	if (!frames || frames.length === 0) {
		return false;
	}
	// Require every frame to have a known filename; anonymous frames are not conclusively third-party.
	return frames.every(
		frame => frame.filename != null && THIRD_PARTY_PATH_PATTERNS.some(pattern => pattern.test(frame.filename ?? ''))
	);
}
